import * as React from 'react';
import {IMT} from '@practicegenius/pg-util/src/StringUtils';
import Echo from 'laravel-echo';
import {
  calculateProgress,
  primaryActionButtonProps,
  PrimaryActionButtonProps,
} from './utils';
import * as registrationMachine from '../machines/accountRegistration';
import lang from '../lang';
import {UseMachineType} from '../machines/useAccountRegistrationMachineType';

type Props = {
  registrationMachine: UseMachineType;
  getWebsocket: () => Echo;
};

type ScreenProps = Props & {
  onFormSubmit: React.ComponentProps<'form'>['onSubmit'];
  lang: typeof lang.confirmEmail;
  progress: number;
  title: string;
  relevantEmail: string;
  useResponsibleEmail: boolean | null;
  actionButtons: {
    primary: PrimaryActionButtonProps;
  };
  resendEmail: () => unknown;
  verificationProps: {
    code: string;
    onChangeVerificationCode: (code: string) => unknown;
    error: string | undefined;
  };
};

export type ConfirmEmailScreenType = React.ComponentType<ScreenProps>;

export const ConfirmEmail = (
  props: Props & {children: ConfirmEmailScreenType}
) => {
  const [{context, matches}, send] = props.registrationMachine;
  const {
    account,
    account: {contactId, useResponsibleEmail},
    emailVerificationCode,
    validationErrors,
    navigator: {location},
  } = context;

  // TODO Is this websocket stuff really isomorphic?
  /**
   * Begin websocket subscriptions for direct email verification via Pusher/Laravel Echo
   */
  const subscribeToWebsocket = React.useCallback(() => {
    props
      .getWebsocket()
      .channel(`contact.${contactId}`)
      .listen('.verified', () => {
        send({
          type: registrationMachine.RegistrationEvent.MARK_EMAIL_VERIFIED,
          field: useResponsibleEmail ? 'responsibleEmail' : 'email',
        });
      });
  }, [contactId, props, send, useResponsibleEmail]);

  const unsubscribeFromWebsocket = React.useCallback(() => {
    if (contactId) {
      props.getWebsocket().leaveChannel(`contact.${contactId}`);
    }
  }, [contactId, props]);

  /**
   * Begin listener for requirements to update via websocket
   */
  React.useEffect(() => {
    subscribeToWebsocket();
    return unsubscribeFromWebsocket;
  }, [subscribeToWebsocket, unsubscribeFromWebsocket]);

  const relevantEmail =
    account[useResponsibleEmail ? 'responsibleEmail' : 'email'] ||
    lang.confirmEmail.defaultRelevantEmail;

  const progress = calculateProgress({
    draftAccount: account,
    location,
  });

  const title = IMT(lang.confirmEmail.title, {
    emailQualifier: useResponsibleEmail
      ? lang.confirmEmail.parentEmailQualifier
      : '',
  });

  const actionButtons = {
    primary: primaryActionButtonProps({
      children: lang.confirmEmail.buttons.primary.title,
      loading: matches({
        [registrationMachine.RegistrationState.emailVerification]:
          registrationMachine.RegistrationState.submittingVerificationCode,
      }),
      disabled: Boolean(
        !emailVerificationCode || validationErrors?.emailVerificationCode
      ),
    }),
  };

  const renderProps: ScreenProps = {
    ...props,
    lang: lang.confirmEmail,
    onFormSubmit: e => {
      e.preventDefault();
      send(registrationMachine.RegistrationEvent.SUBMIT_VERIFICATION_CODE);
    },
    progress,
    title,
    relevantEmail,
    useResponsibleEmail,
    actionButtons,
    resendEmail: () => {
      send(registrationMachine.RegistrationEvent.RESEND_VERIFICATION_EMAIL);
    },
    verificationProps: {
      code: emailVerificationCode,
      onChangeVerificationCode: verificationCode => {
        send({
          type: registrationMachine.RegistrationEvent.UPDATE_DATA,
          payload: {emailVerificationCode: verificationCode},
        });
      },
      error: validationErrors?.emailVerificationCode,
    },
  };

  const Screen = props.children;
  return <Screen {...renderProps} />;
};
